import { migrationOnlySquashOptional } from '@fullstory/util/optional-type-migration';
import * as convert from '@fullstory/search-converter';
import { DateRangeID, Flags, PlatformEnum, SearchCriteria, TimeRange } from '@fullstory/client-api';
import { UID, WithID } from '@fullstory/util/uid';
import { DefaultPartialEventMap, EventConverterMap } from '@fullstory/search-converter/event';
import { UserConverterMap } from '@fullstory/search-converter/user';
import { DependentConverterMap } from '@fullstory/search-converter/dependent';
import {
  defaultEventType,
  defaultNotEventType,
  defaultUserType,
  DependentType,
  EventType,
  NONE,
  Platform as UiPlatform,
  SearchType,
  UserType,
} from '@fullstory/search-types';
import { assertExhaustive, OmitStrict } from '@fullstory/typeutils';
import {
  protoPlatformToUiPlatform,
  uiPlatformToProtoPlatform,
} from '@fullstory/search-converter/helpers';
import { DocumentCriteriaList } from '@fullstory/search-converter/shared';
import {
  Behavior,
  Constraints,
  DependentEventFilter,
  Event,
  EventThreshold,
  Platform,
  SegmentDefinition,
  UserProperty,
} from '@fullstory/proto/fs/api/segment';
import { every, isEqual } from 'lodash';
import { makeSegmentDefinition } from '@fullstory/search-shared';
import { UnnamedSegment } from '@fullstory/proto/fs/services/dx-objects';
import { DateRange, getDateRange } from '@fullstory/util/date';
import { intl } from '@fullstory/intl';
import moment from 'moment';
import * as protobuf from '@fullstory/proto/google/protobuf';

export const MAX_EVENT_FILTERS = 50;

export enum SearchFilterTypes {
  Events = 'Has Events',
  NotEvents = 'Has Not Events',
  Users = 'Users',
}

export type SearchGroup<T extends SearchType> = {
  primary: WithID<T>;
  dependents: WithID<DependentType>[];
  disabled?: boolean; // if true, row will appear in search ui but not url/search criteria
};

interface UserSuggestion {
  category: 'user';
  group: SearchGroup<UserType>;
}

interface EventSuggestion {
  category: 'event' | 'dependent';
  group: SearchGroup<EventType>;
}

export type OmnisearchSuggestion = UserSuggestion | EventSuggestion;

// TimeRange state is currently controlled by the SearchWidgetHost, not the SearchModel
export type SearchModel = {
  name: string;
  description: string;
  events: SearchGroup<EventType>[];
  notEvents: SearchGroup<EventType>[];
  users: SearchGroup<UserType>[];

  // TODO(clay): group these under constraint types
  // positive constraints
  inSameSession?: boolean;
  inEventOrder?: boolean;
  completeSessions?: boolean;
  platform?: PlatformEnum | null;
  withinTime?: protobuf.duration;
  eventThresholds: (EventThreshold | undefined)[];
  // exclude constraints (only has a subset of support right now)
  excludeEventThresholds: (EventThreshold | undefined)[];
};

export type SegmentDefinitionWithDisabled = {
  definition: SegmentDefinition;
  disabled: UnnamedSegment.Disabled;
  name: string;
  description: string;
};

// Returns a deep copy of a SearchGroup, includes new ids
export function cloneSearchGroup(group: SearchGroup<UserType>): SearchGroup<UserType>;
export function cloneSearchGroup(group: SearchGroup<EventType>): SearchGroup<EventType>;
export function cloneSearchGroup(
  group: SearchGroup<UserType | EventType>,
): SearchGroup<UserType | EventType> {
  return {
    ...group,
    primary: {
      ...group.primary,
      id: UID(),
    },
    dependents: group.dependents.slice().map(dt => ({
      ...dt,
      id: UID(),
    })),
  };
}

export const isGroupEqual = <T extends SearchType>(
  a: SearchGroup<T>,
  b: SearchGroup<T>,
  ignoreIds?: boolean,
) => {
  if (ignoreIds) {
    return (
      isEqualWithoutId(a.primary, b.primary) &&
      a.disabled === b.disabled &&
      a.dependents.length === b.dependents.length &&
      every(
        a.dependents.map((ad, i) => {
          const dependent = b.dependents[i];
          return dependent && isEqualWithoutId(ad, dependent);
        }),
      )
    );
  } else {
    return isEqual(a, b);
  }
};

const isEqualWithoutId = <T extends object>(a: WithID<T>, b: WithID<T>) => {
  const { id, ...aa } = a;
  const { id: _, ...bb } = b;
  return isEqual(aa, bb);
};

export function eventModelWithoutPartials(
  events: SearchModel['events'] | SearchModel['notEvents'],
): SearchModel['events'] | SearchModel['notEvents'] {
  return events
    .map(ev => eventGroupWithoutPartials(ev))
    .filter((ev: SearchGroup<EventType> | null): ev is SearchGroup<EventType> => !!ev);
}

export function eventGroupWithoutPartials(
  ev: SearchGroup<EventType>,
): SearchGroup<EventType> | null {
  // First handle primary.
  if (!ev.primary) {
    // remove because no primary is set (the any activity primary still gets a value here)
    // this mainly filters out bogus records
    return null;
  }
  const primaryWithoutPartials = EventConverterMap[ev.primary.kind][
    ev.primary.subkind
  ].withoutPartials(ev.primary, eventGroupWithoutPartials);
  if (
    !primaryWithoutPartials ||
    !(primaryWithoutPartials.ok || DefaultPartialEventMap[ev.primary.kind])
  ) {
    // remove because there is no withoutPartials mapping,
    // or the mapping is not ok and there is no fallback default
    return null;
  }
  // Possibly falling back to the default partial results for primary.
  const primary = primaryWithoutPartials.ok
    ? primaryWithoutPartials.s
    : DefaultPartialEventMap[ev.primary.kind]!.defaultType();
  // Then handle dependents.
  const dependents = ev.dependents
    .map(dependentWithoutPartials)
    .filter((dep): dep is WithID<DependentType> => !!dep);
  // And return the final result.
  return {
    ...ev,
    primary,
    dependents,
  };
}

export function dependentWithoutPartials(dep: WithID<DependentType>): WithID<DependentType> | null {
  const _dependentWithoutPartials = (dep: DependentType) =>
    DependentConverterMap[dep.kind][dep.subkind].withoutPartials(dep, _dependentWithoutPartials);
  const res = _dependentWithoutPartials(dep);
  return res?.ok ? res.s : null;
}

export function userModelWithoutPartials(users: SearchModel['users']): SearchModel['users'] {
  const userWithoutPartials = (user: UserType) =>
    UserConverterMap[user.kind][user.subkind].withoutPartials(user, userWithoutPartials);
  return users
    .map(user => ({ user, withoutPartials: userWithoutPartials(user.primary) }))
    .filter(tuple => tuple.withoutPartials?.ok)
    .map(tuple => ({
      ...tuple.user,
      primary: tuple.withoutPartials.s,
    }));
}

// Applies partial detectors defined in the meta objects to strip the partial representations.
// Useful to call prior to constucting the document criterion, or for deriving a valid-model from a ui representation.
export function modelWithoutPartials(model: SearchModel): SearchModel {
  return {
    ...model,
    events: eventModelWithoutPartials(model.events),
    notEvents: eventModelWithoutPartials(model.notEvents),
    users: userModelWithoutPartials(model.users),
  };
}

export const eventModelToEvents = (eventModel: SearchGroup<EventType>[]): Event[] =>
  eventModelWithoutPartials(eventModel)
    .filter(ev => !ev.disabled)
    .map((ev): Event => eventGroupToProtoEvent(ev));

export function modelToSegmentDefinition(model: SearchModel): SegmentDefinition {
  return modelToSegmentDefinitionWithDisabled(model).definition;
}

export function modelToSegmentDefinitionWithDisabled(
  model: SearchModel,
): SegmentDefinitionWithDisabled {
  // TODO(ryan|brad) remove PlatformEnum entirely after we are finished with doc criterion.
  function platformEnumToUiPlatform(pe: PlatformEnum | null | undefined): UiPlatform {
    switch (pe) {
      case PlatformEnum.PlatformNativeMobile:
        return UiPlatform.nativemobile;
      case PlatformEnum.PlatformWeb:
        return UiPlatform.web;
      default:
        // captures legacy undefined
        return UiPlatform.any;
    }
  }

  function userModelToProperties(model: SearchGroup<UserType>[], forDisabled: boolean = false) {
    return model
      .map((u, i) => ({ u, i }))
      .filter(u => !!u.u.disabled === forDisabled)
      .map(u => ({ filter: { primary: convert.userTypeToFilter(u.u.primary) }, index: u.i }))
      .filter(f => !!f.filter.primary);
  }

  function evtModelToBehaviors(
    model: SearchGroup<EventType>[],
    opts?: { allowAny?: boolean; forDisabled?: boolean },
  ) {
    return model
      .map((ev, i) => ({ ev, i }))
      .filter(ev => !!ev.ev.disabled === !!opts?.forDisabled)
      .map(ev => ({
        filter: {
          event: {
            primary: convert.eventTypeToFilter(ev.ev.primary),
            deps: ev.ev.dependents.map(dep => convert.dependentTypeToFilter(dep)),
          },
        },
        index: ev.i,
      }))
      .filter(f => !!opts?.allowAny || !!f.filter.event.primary || !!f.filter.event.deps.length);
  }

  const behaviors = evtModelToBehaviors(model.events, { allowAny: model.inEventOrder });
  const eventThresholds = applicableEventThresholds(model.eventThresholds, behaviors);
  const constraints: Constraints = {
    inOrder: model.inEventOrder ?? false,
    inSameSession: model.inSameSession ?? false,
    platform: uiPlatformToProtoPlatform(platformEnumToUiPlatform(model.platform)),
    timeRange: null,
    completeSessions: model.completeSessions ?? false,
    withinTime: model.withinTime ?? protobuf.Duration.zero,
    eventThresholds,
  };

  const excludeBehaviors = evtModelToBehaviors(model.notEvents);
  const excludeEventThresholds = applicableEventThresholds(
    model.excludeEventThresholds,
    excludeBehaviors,
  );
  const excludeConstraints = applicableExcludeConstraints(excludeEventThresholds, constraints);

  const definition = {
    userProperties: userModelToProperties(model.users).map(u => u.filter),
    excludeUserProperties: [], // TODO: add once we start using user exclusions.
    behaviors: behaviors.map(ev => ev.filter),
    excludeBehaviors: excludeBehaviors.map(ev => ev.filter),
    constraints,
    excludeConstraints,
  };
  // TODO(clay): handle disabled for event thresholds
  const disabled: UnnamedSegment.Disabled = {
    userProperties: userModelToProperties(model.users, true).map(u => ({
      index: u.index,
      value: u.filter,
    })),
    excludeUserProperties: [],
    behaviors: evtModelToBehaviors(model.events, {
      allowAny: model.inEventOrder,
      forDisabled: true,
    }).map(ev => ({
      index: ev.index,
      value: ev.filter,
    })),
    excludeBehaviors: evtModelToBehaviors(model.notEvents, { forDisabled: true }).map(ev => ({
      index: ev.index,
      value: ev.filter,
    })),
  };

  return {
    definition,
    disabled,
    name: model.name,
    description: model.description,
  };
}

interface IndexedBehavior {
  index: number;
}

export function applicableEventThresholds(
  eventThresholds: (EventThreshold | undefined)[],
  indexedBehaviors: IndexedBehavior[],
): EventThreshold[] {
  const allInapplicable = eventThresholds.every(
    et => !et || protobuf.int64.toNumber(et.occurrences) === 1,
  );
  return allInapplicable
    ? // if all occurrences are undefined or 1, we don't need any thresholds
      []
    : // otherwise, produce the event thresholds for the provided behaviors, falling back to 1 occurrence for unset thresholds
      indexedBehaviors.map(
        b =>
          eventThresholds[b.index] ?? {
            occurrences: protobuf.int64.fromNumber(1),
            withinTime: protobuf.Duration.zero,
          },
      );
}

export function applicableExcludeConstraints(
  eventThresholds: EventThreshold[],
  constraints: Constraints,
): Constraints | null {
  if (!eventThresholds.length) {
    return null;
  }
  return {
    // take most of the positive constraint fields verbatim
    ...constraints,
    // we don't want to carry over ordering or within time settings though
    inOrder: false,
    withinTime: protobuf.Duration.zero,
    // and the only supported exclude constraint right now is event thresholds
    eventThresholds,
  };
}

export function getModelFromEvent(
  event: Event | null | undefined,
  flags: Flags,
): SearchGroup<EventType>[] {
  if (!event) {
    return segmentDefinitionToModel(makeSegmentDefinition({ constraints: null }), '', '', flags)
      .events;
  }
  return segmentDefinitionToModel(
    {
      userProperties: [],
      excludeBehaviors: [],
      behaviors: [
        {
          event: guardEvent(event),
        },
      ],
      excludeUserProperties: [],
      constraints: null,
      excludeConstraints: null,
    },
    '',
    '',
    flags,
  ).events;
}

function guardEvent(event: Event | null | undefined): Event | null {
  if (!event) return null;
  if (!event.primary) {
    return {
      ...event,
      primary: {},
    };
  }
  return event;
}

export function getModelFromUserProperty(
  user: UserProperty,
  flags: Flags,
): SearchGroup<UserType>[] {
  return segmentDefinitionToModel(
    {
      userProperties: [user],
      excludeBehaviors: [],
      behaviors: [],
      excludeUserProperties: [],
      constraints: null,
      excludeConstraints: null,
    },
    '',
    '',
    flags,
  ).users;
}

export function segmentDefinitionToModel(
  def: SegmentDefinition,
  name: string,
  description: string,
  flags: Flags,
): SearchModel {
  const noneDisabled = {
    userProperties: [],
    excludeUserProperties: [],
    behaviors: [],
    excludeBehaviors: [],
  };
  return segmentDefinitionWithDisabledToModel(
    { definition: def, disabled: noneDisabled, name, description },
    flags,
  );
}

export function segmentDefinitionWithDisabledToModel(
  def: SegmentDefinitionWithDisabled,
  flags: Flags,
): SearchModel {
  // TODO(ryan|brad) remove PlatformEnum entirely after we are finished with doc criterion.
  function uiPlatformToPlatformEnum(plat: UiPlatform): PlatformEnum | undefined {
    switch (plat) {
      case UiPlatform.any:
        return undefined; // legacy support.
      case UiPlatform.nativemobile:
        return PlatformEnum.PlatformNativeMobile;
      case UiPlatform.web:
        return PlatformEnum.PlatformWeb;
      case UiPlatform.server:
        return PlatformEnum.PlatformServer;
      default:
        assertExhaustive(plat);
    }
  }

  function userPropToModel(userProp: UserProperty, disabled?: boolean): SearchGroup<UserType> {
    const g: SearchGroup<UserType> = {
      primary: { id: UID(), ...convert.userFilterToUserType(userProp.primary!, flags) },
      dependents: [], // TODO: add dependents when they are part of the model.
    };
    if (disabled) {
      g.disabled = true;
    }
    return g;
  }

  function behToModel(beh: Behavior, disabled?: boolean): SearchGroup<EventType> {
    const g: SearchGroup<EventType> = {
      primary: { id: UID(), ...convert.eventFilterToEventType(beh.event!.primary!, flags) },
      dependents: beh.event!.deps.map(dep => ({
        id: UID(),
        ...convert.dependentFilterToDependentType(dep, flags),
      })),
    };
    if (disabled) {
      g.disabled = true;
    }
    return g;
  }

  // Keep only event thresholds w/ at least a count of 2 (occurrences = 1 is implied by default and can't be time constrained).
  // Clear out the rest (by setting to undefined).
  const eventThresholds = (def.definition.constraints?.eventThresholds ?? []).map(et =>
    protobuf.int64.toNumber(et.occurrences) > 1 ? et : undefined,
  );
  const excludeEventThresholds = (def.definition.excludeConstraints?.eventThresholds ?? []).map(
    et => (protobuf.int64.toNumber(et.occurrences) > 1 ? et : undefined),
  );

  const sm: SearchModel = {
    name: def.name,
    description: def.description,
    events: def.definition.behaviors.map(beh => behToModel(beh)),
    notEvents: def.definition.excludeBehaviors.map(beh => behToModel(beh)),
    users: def.definition.userProperties.map(userProp => userPropToModel(userProp)),
    inSameSession: def.definition.constraints?.inSameSession || false,
    inEventOrder: def.definition.constraints?.inOrder || false,
    completeSessions: def.definition.constraints?.completeSessions || false,
    platform: uiPlatformToPlatformEnum(
      protoPlatformToUiPlatform(def.definition.constraints?.platform || Platform.PLATFORM_INVALID),
    ),
    withinTime: def.definition.constraints?.withinTime || protobuf.Duration.zero,
    eventThresholds,
    excludeEventThresholds,
    // TODO: How to extract time range?
  };

  // throw back in any disabled filters as well
  def.disabled.behaviors.forEach(beh => {
    sm.events.splice(beh.index, 0, behToModel(beh.value!, true));
    if (beh.index < sm.eventThresholds.length) {
      // also need to push out event thresholds for proper alignment
      sm.eventThresholds.splice(beh.index, 0, undefined);
    }
  });
  def.disabled.excludeBehaviors.forEach(beh => {
    sm.notEvents.splice(beh.index, 0, behToModel(beh.value!, true));
    if (beh.index < sm.excludeEventThresholds.length) {
      // also need to push out event thresholds for proper alignment
      sm.excludeEventThresholds.splice(beh.index, 0, undefined);
    }
  });
  def.disabled.userProperties.forEach(userProp =>
    sm.users.splice(userProp.index, 0, userPropToModel(userProp.value!, true)),
  );

  if (sm.users.length === 0)
    sm.users = [{ primary: { id: UID(), ...defaultUserType }, dependents: [] }];
  if (sm.events.length === 0)
    sm.events = [{ primary: { id: UID(), ...defaultEventType }, dependents: [] }];
  if (sm.notEvents.length === 0)
    sm.notEvents = [{ primary: { id: UID(), ...defaultNotEventType }, dependents: [] }];
  return sm;
}

export function eventGroupToProtoEvent(eventGroup: SearchGroup<EventType>): Event {
  return {
    primary: convert.eventTypeToFilter(eventGroup.primary),
    deps: eventGroup.dependents.map(dep => convert.dependentTypeToFilter(dep)),
  };
}

export function protoEventToEventGroup(event: Event, flags: Flags): SearchGroup<EventType> {
  return {
    primary: { id: UID(), ...convert.eventFilterToEventType(event.primary, flags) },
    dependents: event.deps.map(dep => ({
      id: UID(),
      ...convert.dependentFilterToDependentType(dep, flags),
    })),
  };
}

export function eventCriteriaToGroup(
  criteria: DocumentCriteriaList,
  flags: Flags,
): SearchGroup<EventType>[] {
  const events: SearchGroup<EventType>[] = [];
  (criteria || []).forEach(ec => {
    if (!ec) {
      return;
    }

    const eventType = convert.fromEventCriteria(convert.maybeReorderCriteria(ec), flags);
    // Failed to convert to EventType
    if (!eventType.ok) {
      return;
    }
    events.push({
      primary: {
        id: UID(),
        ...(eventType.primary as EventType),
      },
      dependents: eventType.dependents.map(dep => ({
        id: UID(),
        ...dep,
      })),
    });
  });

  return events;
}

export function criteriaToModel(
  criteria: OmitStrict<SearchCriteria, 'TimeRange'>,
  flags: Flags,
): SearchModel {
  const events: SearchGroup<EventType>[] = eventCriteriaToGroup(
    criteria.EventCriteria as DocumentCriteriaList,
    flags,
  );
  const notEvents: SearchGroup<EventType>[] = eventCriteriaToGroup(
    criteria.EventNeverCriteria as DocumentCriteriaList,
    flags,
  );
  const users: SearchGroup<UserType>[] = [];

  (criteria.UserTipCriteria || []).forEach(uc => {
    const userType = convert.fromUserTipCriteria([migrationOnlySquashOptional(uc)], flags);
    // Failed to convert to UserType
    if (userType == null) {
      return;
    }
    users.push({
      primary: {
        id: UID(),
        ...userType,
      },
      dependents: [],
    });
  });

  return {
    name: '',
    description: '',
    events,
    notEvents,
    users,
    inSameSession: criteria.InSameSession,
    inEventOrder: criteria.EnforceEventOrder,
    completeSessions: false, // Not supported in legacy model
    platform: criteria.Platform,
    withinTime: protobuf.Duration.zero, // Not supported in legacy model
    eventThresholds: [], // Not supported in legacy model
    excludeEventThresholds: [], // Not supported in legacy model
  };
}

// this should stay in sync with computer.go's Defined TimeRanges
export const dateRangeById = {
  [DateRangeID.AllTime]: {
    id: DateRangeID.AllTime,
    name: intl.formatMessage({ defaultMessage: 'All time', id: 's+lPP3' }),
    range: { Start: '', End: '' },
  },
  [DateRangeID.Today]: {
    id: DateRangeID.Today,
    name: intl.formatMessage({ defaultMessage: 'Today', id: 'zWgbGg' }),
    range: { Start: 'NOW/DAY', End: 'NOW/DAY+1DAY' },
  },
  [DateRangeID.Yesterday]: {
    id: DateRangeID.Yesterday,
    name: intl.formatMessage({ defaultMessage: 'Yesterday', id: '6dIxDP' }),
    range: { Start: 'NOW/DAY-1DAY', End: 'NOW/DAY' },
  },
  [DateRangeID.Past60Minutes]: {
    id: DateRangeID.Past60Minutes,
    name: intl.formatMessage({ defaultMessage: 'Past 60 minutes', id: 'dycj1L' }),
    range: { Start: 'NOW/MINUTE-59MINUTE', End: 'NOW/MINUTE+1MINUTE' },
  },
  [DateRangeID.Past3Hours]: {
    id: DateRangeID.Past3Hours,
    name: intl.formatMessage({ defaultMessage: 'Past 3 hours', id: 'f9/17e' }),
    range: { Start: 'NOW/HOUR-2HOUR', End: 'NOW/HOUR+1HOUR' },
  },
  [DateRangeID.Past6Hours]: {
    id: DateRangeID.Past6Hours,
    name: intl.formatMessage({ defaultMessage: 'Past 6 hours', id: '/WVb/X' }),
    range: { Start: 'NOW/HOUR-5HOUR', End: 'NOW/HOUR+1HOUR' },
  },
  [DateRangeID.Past24Hours]: {
    id: DateRangeID.Past24Hours,
    name: intl.formatMessage({ defaultMessage: 'Past 24 hours', id: '/a7cg5' }),
    range: { Start: 'NOW/HOUR-23HOUR', End: 'NOW/HOUR+1HOUR' },
  },
  [DateRangeID.PastWeek]: {
    id: DateRangeID.PastWeek,
    name: intl.formatMessage({ defaultMessage: 'Past 7 days', id: 'mbbNJS' }),
    range: { Start: 'NOW/DAY-6DAY', End: 'NOW/DAY+1DAY' },
  },
  [DateRangeID.PastMonth]: {
    id: DateRangeID.PastMonth,
    name: intl.formatMessage({ defaultMessage: 'Past 30 days', id: 'OIM20v' }),
    range: { Start: 'NOW/DAY-29DAY', End: 'NOW/DAY+1DAY' },
  },
  [DateRangeID.Past90Days]: {
    id: DateRangeID.Past90Days,
    name: intl.formatMessage({ defaultMessage: 'Past 90 days', id: 'ZOWtIg' }),
    range: { Start: 'NOW/DAY-89DAY', End: 'NOW/DAY+1DAY' },
  },
  [DateRangeID.PastYear]: {
    id: DateRangeID.PastYear,
    name: intl.formatMessage({ defaultMessage: 'Past 12 months', id: 'o0vS8L' }),
    range: { Start: 'NOW/DAY-364DAY', End: 'NOW/DAY+1DAY' },
  },
};

export const sinceDateRangeEnd = 'NOW/DAY+1DAY';

export function getDateRangeIdFromTimeRange(range: TimeRange): DateRangeID {
  if (!range) {
    return DateRangeID.PastMonth;
  }

  for (const d of Object.values(dateRangeById)) {
    if (range.Start == d.range.Start && range.End == d.range.End) {
      return d.id;
    }
  }

  // If we got here, the DateRangeID is either 'Custom' or 'Since'
  return range.End === sinceDateRangeEnd ? DateRangeID.Since : DateRangeID.Custom;
}

/**
 * getDateRangeFromTimeRange will return start and end dates (which may be undefined) given a TimeRange.
 *
 * For both timeRange.Start and timeRange.End, there are essentially 3 scenarios:
 *   1. the date is an empty string (''). This will yield undefined
 *   2. the date is an ISO string version of a Date. This will yield a Date object via moment
 *   3. the date is a SOLR string (relative date). This will yield a Date object via search utils
 *
 * This function helps 'initialize' the values of the Since and Custom
 * DatePickers so that the selected date range is carried over when switching options.
 * (eg. switching from "Past Week" to "Custom" will pre-populate the custom
 * dates to the dates of the Past Week)
 */
export const getDateRangeFromTimeRange = (timeRange?: TimeRange): DateRange => {
  // If no timeRange is passed in, return undefined dates. The DatePicker will render these as empty inputs with no date selected
  if (!timeRange) return { startTime: null, endTime: null };

  // First, get the DateRangeID that matches this TimeRange
  const dateRangeID = getDateRangeIdFromTimeRange(timeRange);

  // If the DateRangeID is Since, the startDate is assumed to be an empty string or an ISO string (scenarios 1 & 2)
  // and the endDate will use the search util to fetch the correct moment date for 'Today'
  if (dateRangeID === DateRangeID.Since) {
    return {
      startTime: timeRange.Start === '' ? null : moment(timeRange.Start).toDate(),
      endTime: getDateRange(DateRangeID.Today, new Date()).endTime ?? new Date(),
    };
    // If the DateRangeID is Custom or AllTime, the startDate and endDate are assumed to be an empty string or an ISO string (scenarios 1 & 2)
  } else if (dateRangeID === DateRangeID.Custom || dateRangeID === DateRangeID.AllTime) {
    return {
      startTime: timeRange.Start === '' ? null : moment(timeRange.Start).toDate(),
      endTime: timeRange.End === '' ? null : moment(timeRange.End).toDate(),
    };
  }

  // All other DateRangeID scenarios means that the TimeRange strings are SOLR relative dates (scenario 3)
  // Uses the search utils to convert said SOLR strings into JavaScript Dates and fallback to undefined
  return getDateRange(dateRangeID, new Date());
};

export function defaultModel(): SearchModel {
  return {
    name: '',
    description: '',
    events: [
      {
        primary: { ...defaultEventType, id: UID() },
        dependents: [],
      },
    ],
    notEvents: [
      {
        primary: { ...defaultNotEventType, id: UID() },
        dependents: [],
      },
    ],
    users: [
      {
        primary: { ...defaultUserType, id: UID() },
        dependents: [],
      },
    ],
    inSameSession: false,
    inEventOrder: false,
    completeSessions: false,
    platform: undefined,
    withinTime: protobuf.Duration.zero,
    eventThresholds: [],
    excludeEventThresholds: [],
  };
}

export function defaultEventModel(filterType: SearchFilterTypes): SearchGroup<EventType>[] {
  return [
    {
      primary: {
        ...(filterType === SearchFilterTypes.Events ? defaultEventType : defaultNotEventType),
        id: UID(),
      },
      dependents: [],
    },
  ];
}

export const eventStateToEventType = (
  event: Event | undefined,
  flags: Flags | null,
  disabled?: boolean,
): SearchGroup<EventType> => {
  if (!event || (!event.primary && event.deps?.length === 0) || !flags) {
    return {
      primary: {
        kind: 'any',
        subkind: NONE,
        value: NONE,
        id: UID(),
      },
      dependents: [],
      disabled,
    };
  }
  return {
    primary: {
      ...convert.eventFilterToEventType(event.primary, flags),
      id: UID(),
    },
    dependents: event.deps.map(dependent => ({
      ...convert.dependentFilterToDependentType(dependent, flags),
      id: UID(),
    })),
  };
};

export const eventTypeToEventState = (event: SearchGroup<EventType>): Event => {
  if (event.primary.kind === 'any')
    return { primary: null, deps: event.dependents.map(convert.dependentTypeToFilter) };
  return {
    primary: convert.eventTypeToFilter(event.primary),
    deps: event.dependents.map(convert.dependentTypeToFilter),
  };
};

export const dependentTypesToDependentState = (deps: DependentType[]): DependentEventFilter[] =>
  deps.map(convert.dependentTypeToFilter);

/**
 * tryConvertUserToDependentSuggestion attempts to convert the omnisearch suggestion from a
 * UserSuggestion to a EventSuggestion. Since metrics and funnels both don't have a concept of a
 * user row, the user type is not suited for the omnisearch when those modes are enabled.
 *
 * @param suggestion suggestion to be converted
 * @returns either a converted suggestion or the original suggestion unchanged
 */
export const tryConvertUserToDependentSuggestion = (
  suggestion: OmnisearchSuggestion,
): OmnisearchSuggestion => {
  const castType = suggestion.group?.primary as WithID<DependentType>;
  if (castType && castType.kind in DependentConverterMap) {
    return {
      ...suggestion,
      category: 'dependent',
      group: {
        primary: { ...defaultEventType, id: UID() },
        dependents: [castType],
      },
    };
  }
  return suggestion;
};
